import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './AuditorHelpPanel.css';

/**
 * Auditor "Help" sub-agent panel.
 *
 * A deterministic, grounded question-answer surface over the Auditor section.
 * The backend (/api/opo/auditor-help) retrieves from the on-disk Auditor
 * records with BM25 and answers with Qwen 2.5 14B under greedy decoding, so the
 * same question over the same records returns the same answer. Every answer
 * ships with the exact source files it used.
 */

const SUGGESTED = [
  'Are we registered for GST, and when is our next GST return due?',
  'What was our first GST return and how much did we remit?',
  'What is our T2 fiscal year end and has the T2 been filed?',
  'Are we registered for BC PST? Why or why not?',
  'What SR&ED amounts did we claim?',
  'Who are the directors and shareholders of OptiPrime AI Inc.?',
];

function fileViewUrl(relPath) {
  return `/api/opo/auditor/compliance-document/view?file=${encodeURIComponent(relPath)}`;
}

function Citations({ citations }) {
  const [open, setOpen] = useState(null);
  if (!citations || citations.length === 0) return null;
  return (
    <div className="ahelp-citations">
      <div className="ahelp-citations-title">Sources ({citations.length})</div>
      {citations.map((c) => (
        <div key={c.n} className="ahelp-citation">
          <button
            type="button"
            className="ahelp-citation-head"
            onClick={() => setOpen(open === c.n ? null : c.n)}
          >
            <span className="ahelp-citation-num">[{c.n}]</span>
            <span className="ahelp-citation-path">{c.path}{c.heading ? ` — ${c.heading}` : ''}</span>
            <span className="ahelp-citation-score" title="BM25 relevance score">{c.score}</span>
            <span className="ahelp-citation-arrow">{open === c.n ? '▲' : '▼'}</span>
          </button>
          {open === c.n && (
            <div className="ahelp-citation-body">
              <pre className="ahelp-citation-snippet">{c.snippet}{c.snippet && c.snippet.length >= 320 ? '…' : ''}</pre>
              <a href={fileViewUrl(c.path)} target="_blank" rel="noopener noreferrer" className="ahelp-citation-link">
                Open source file →
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const AuditorHelpPanel = () => {
  const [status, setStatus] = useState(null);
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const [exchanges, setExchanges] = useState([]); // { q, answer, grounded, refusal, lowConfidence, citations, model }
  const transcriptRef = useRef(null);

  useEffect(() => {
    fetch('/api/opo/auditor-help/status')
      .then((r) => r.json())
      .then(setStatus)
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => {
    if (transcriptRef.current) transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [exchanges, asking]);

  const ask = useCallback(async (q) => {
    const query = (q != null ? q : question).trim();
    if (!query || asking) return;
    setError('');
    setAsking(true);
    setQuestion('');
    try {
      const r = await fetch('/api/opo/auditor-help/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: query }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Request failed');
      setExchanges((prev) => [...prev, { q: query, ...d }]);
    } catch (e) {
      setError(e.message);
    }
    setAsking(false);
  }, [question, asking]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  return (
    <div className="ahelp-panel">
      <div className="ahelp-header">
        <div className="ahelp-title-row">
          <span className="ahelp-icon">🛟</span>
          <span className="ahelp-title">Help</span>
          <span className="ahelp-subtitle">Deterministic answers grounded only in the Auditor records</span>
        </div>
        {status && (
          <div className="ahelp-status">
            <span className="ahelp-chip ahelp-chip--model">{status.model}</span>
            <span className="ahelp-chip">{status.fileCount} files · {status.chunkCount} passages</span>
            <span className="ahelp-chip ahelp-chip--det">Deterministic · greedy decode</span>
          </div>
        )}
      </div>

      <div className="ahelp-note">
        This agent answers <strong>only</strong> from documents under the Auditor section
        (taxes, invoices, expenses, compliance, SR&amp;ED, financial statements, assets, receipts and the
        sub-agent runbooks). It does not use outside knowledge and will say so when the records don&apos;t cover
        a question. Every answer lists the exact source files used.
      </div>

      <div className="ahelp-transcript" ref={transcriptRef}>
        {exchanges.length === 0 && !asking && (
          <div className="ahelp-empty">
            <div className="ahelp-empty-title">Ask about anything in the Auditor records</div>
            <div className="ahelp-suggested">
              {SUGGESTED.map((s) => (
                <button key={s} type="button" className="ahelp-suggested-chip" onClick={() => ask(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {exchanges.map((ex, i) => (
          <div key={i} className="ahelp-exchange">
            <div className="ahelp-q"><span className="ahelp-q-label">Q</span>{ex.q}</div>
            <div className={`ahelp-a${ex.refusal ? ' ahelp-a--refusal' : ''}`}>
              <div className="ahelp-a-badges">
                {ex.refusal ? (
                  <span className="ahelp-badge ahelp-badge--refusal">Not in records</span>
                ) : (
                  <span className="ahelp-badge ahelp-badge--grounded">Grounded</span>
                )}
                {!ex.refusal && ex.lowConfidence && (
                  <span className="ahelp-badge ahelp-badge--low" title="Weak lexical match — verify against the cited sources">Low confidence</span>
                )}
                {ex.model && <span className="ahelp-badge ahelp-badge--model">{ex.model}</span>}
              </div>
              <div className="ahelp-a-body">
                <ReactMarkdown>{ex.answer}</ReactMarkdown>
              </div>
              <Citations citations={ex.citations} />
            </div>
          </div>
        ))}

        {asking && (
          <div className="ahelp-thinking">
            <span className="ahelp-dot" /><span className="ahelp-dot" /><span className="ahelp-dot" />
            <span className="ahelp-thinking-text">Searching the Auditor records…</span>
          </div>
        )}
      </div>

      {error && <div className="ahelp-error">{error}</div>}

      <div className="ahelp-composer">
        <textarea
          className="ahelp-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask a question about the Auditor records… (Enter to send, Shift+Enter for a new line)"
          rows={2}
          disabled={asking}
        />
        <button type="button" className="ahelp-send" onClick={() => ask()} disabled={asking || !question.trim()}>
          {asking ? 'Asking…' : 'Ask'}
        </button>
      </div>
    </div>
  );
};

export default AuditorHelpPanel;
